import { INTEGRATION_INBOUND_SOURCE_KEY } from "#app/components/automations/trigger-sentence";
import { applySubject } from "#app/components/automations/canvas/automation-step-labels";
import {
  outboundActionOf,
  outboundConnectionIdOf,
} from "#app/hooks/automations/use-automation-outbound-targets";

// How a connection-bound trigger or step is NAMED in one line, everywhere a
// compact label is needed: the Manage list's chips, the workflow popover's card
// sentence, the read-only text summary, and the auto-generated automation title.
//
// A connection-bound node's generic catalog label ("Integration event
// received" / "Integration action") is identical on every such automation and
// says nothing about which system it touches — these name the event/action and
// its connection instead. Both fall back to the generic label whenever the
// connection list hasn't loaded or the connection has since been deleted, so a
// row never renders a raw id.
//
// The canvas NODE deliberately doesn't use these: it has two lines (title +
// caption) and splits the same parts across them.

type ResolveFn = (entityType: string, id: string) => string | null;

/** "Connection triggered from AccuLynx SW Contracts" (or `generic`). */
export function inboundTriggerPhrase(
  trigger: { sourceKey: string; config?: unknown } | null | undefined,
  resolveInbound: ResolveFn,
  generic: string,
): string {
  if (trigger?.sourceKey !== INTEGRATION_INBOUND_SOURCE_KEY) return generic;
  const config = (trigger.config ?? {}) as Record<string, unknown>;
  const topic = typeof config.topic === "string" ? config.topic : null;
  const connectionId =
    typeof config.connectionId === "string" ? config.connectionId : null;
  const event = (topic ? resolveInbound("inboundTopic", topic) : null) ?? topic ?? generic;
  const connection = connectionId ? resolveInbound("connection", connectionId) : null;
  return connection ? `${event} from ${connection}` : event;
}

// "Log connection action · Bartlett Main" — the COLLAPSED form, for the Action
// node's merged Action dropdown. Every outbound provider currently ships the
// same action key, so the action label alone cannot say which connection a step
// targets; the open list gets the connection from its subsection heading, the
// closed trigger has only this one line. A connection whose name is unknown (not
// yet loaded, since deleted) reads as the bare action.
export function outboundActionTriggerLabel(
  actionLabel: string,
  connectionLabel: string | null | undefined,
): string {
  const connection = connectionLabel?.trim();
  return connection ? `${actionLabel} · ${connection}` : actionLabel;
}

/**
 * A provider action's label, ready to render.
 *
 * THE way to read one. A provider ships `{subject}` in an action label exactly
 * as the step catalog does ("Send {subject} message"), so the raw string is
 * never what a person should see — and `resolveOutbound` is a data lookup that
 * cannot know the automation's noun. Every surface that names an action goes
 * through here, rather than each remembering to substitute: the canvas card and
 * the auto-generated automation title both shipped reading "Send {subject}
 * message" verbatim before it existed.
 */
export function outboundActionLabel(
  resolveOutbound: ResolveFn,
  action: string,
  subject: string,
  generic: string,
): string {
  return applySubject(resolveOutbound("outboundAction", action) ?? generic, subject);
}

/** "Log connection action with AccuLynx SW Contracts" (or `generic`). */
export function outboundStepPhrase(
  step: { type?: string | null; config?: unknown } | null | undefined,
  resolveOutbound: ResolveFn,
  generic: string,
  subject: string,
): string {
  const action = outboundActionOf(step ?? null);
  if (!action) return generic;
  const label = outboundActionLabel(resolveOutbound, action, subject, generic);
  const connectionId = outboundConnectionIdOf(step ?? null);
  const connection = connectionId ? resolveOutbound("connection", connectionId) : null;
  return connection ? `${label} with ${connection}` : label;
}
